import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

interface SelectItem { label: string; value: any; }

@Component({
  selector: 'app-dashboard-grafico-linha',
  templateUrl: './dashboard-grafico-linha.component.html',
  styleUrls: ['./dashboard-grafico-linha.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardGraficoLinhaComponent implements OnInit, OnDestroy, OnChanges {

  @Input() anoSelecionado: number = new Date().getFullYear();
  @Input() dadosReceitas: number[] = new Array(12).fill(0);
  @Input() dadosDespesas: number[] = new Array(12).fill(0);
  @Input() loading = false;
  @Input() currencySymbol = 'PYG';
  @Input() currencyDigits = '1.0-0';
  @Input() currencyLocale = 'es-PY';

  @Output() anoChanged = new EventEmitter<number>();

  texts = {
    estatistica_financeira_anual: '',
    vendas_e_entradas: '',
    compras_e_gastos: ''
  };

  anos: SelectItem[] = [];
  chartData: any;
  chartOptions: any;

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildChart();
      this.cdr.markForCheck();
    });
    this.buildAnos();
    this.buildChart();
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dadosReceitas'] || changes['dadosDespesas'] || changes['currencySymbol'] || changes['currencyDigits'] || changes['currencyLocale']) {
      this.buildChart();
      this.cdr.markForCheck();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      estatistica_financeira_anual: this.translate.instant('dashboard.texts.estatistica_financeira_anual'),
      vendas_e_entradas: this.translate.instant('dashboard.texts.vendas_e_entradas'),
      compras_e_gastos: this.translate.instant('dashboard.texts.compras_e_gastos')
    };
  }

  private buildAnos(): void {
    const currentYear = new Date().getFullYear();
    this.anos = [];
    for (let y = currentYear; y >= 2000; y--) {
      this.anos.push({ label: String(y), value: y });
    }
  }

  private buildChart(): void {
    const labels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
                    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    this.chartData = {
      labels,
      datasets: [
        {
          label: this.texts.vendas_e_entradas || 'Vendas e Entradas',
          data: this.dadosReceitas,
          borderColor: '#165c7d',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          tension: 0,
        },
        {
          label: this.texts.compras_e_gastos || 'Compras e Gastos',
          data: this.dadosDespesas,
          borderColor: 'rgba(158, 158, 158, 0.5)',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 4,
          fill: false,
          tension: 0,
        }
      ]
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 8,
          right: 16,
          top: 24,
          bottom: 8
        }
      },
      clip: true,
      plugins: {
        // Legenda nativa desativada — o template ja exibe legenda customizada (.legenda-row)
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.parsed?.y ?? 0;
              const formatted = this.formatarValorMoeda(val);
              return `${ctx.dataset.label}: ${formatted}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: { display: true },
          ticks: { autoSkip: false }
        },
        y: {
          min: 0,
          grid: { display: true },
          ticks: {
            // Formata valores grandes: acima de 1 bilhao → B, acima de 1 milhao → M, acima de 1 mil → K
            callback: (value: number) => this.formatarValorEixoY(value),
            maxTicksLimit: 6
          }
        }
      }
    };
  }

  // Formata valor monetario usando locale e casas decimais da moeda selecionada
  private formatarValorMoeda(valor: number): string {
    const casasDecimais = this.currencyDigits === '1.0-0' ? 0 : 2;
    return valor.toLocaleString(this.currencyLocale, {
      minimumFractionDigits: casasDecimais,
      maximumFractionDigits: casasDecimais
    });
  }

  // Mapeia codigo ISO da moeda para o simbolo visual exibido no eixo Y
  private obterSimboloMoeda(): string {
    const mapa: Record<string, string> = {
      'PYG': '₲',
      'BRL': 'R$',
      'USD': '$'
    };
    return mapa[this.currencySymbol] ?? this.currencySymbol;
  }

  // Formata valores do eixo Y de forma compacta para evitar corte: B / M / K
  // Prefixo com o simbolo monetario da moeda configurada na empresa
  private formatarValorEixoY(valor: number): string {
    const simbolo = this.obterSimboloMoeda();
    const casasDecimais = this.currencyDigits === '1.0-0' ? 0 : 2;
    if (Math.abs(valor) >= 1_000_000_000) {
      const v = valor / 1_000_000_000;
      return `${simbolo} ${v.toLocaleString(this.currencyLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}B`;
    }
    if (Math.abs(valor) >= 1_000_000) {
      const v = valor / 1_000_000;
      return `${simbolo} ${v.toLocaleString(this.currencyLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
    }
    if (Math.abs(valor) >= 1_000) {
      const v = valor / 1_000;
      return `${simbolo} ${v.toLocaleString(this.currencyLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}K`;
    }
    return `${simbolo} ${valor.toLocaleString(this.currencyLocale, {
      minimumFractionDigits: casasDecimais,
      maximumFractionDigits: casasDecimais
    })}`;
  }

  onAnoChange(event: any): void {
    this.anoChanged.emit(event.value);
  }
}
